import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function OrbitalDiagramGenerator() {
  const [atomicNumber, setAtomicNumber] = useState("");
  const [result, setResult] = useState<string[] | null>(null);

  const generateOrbitalDiagram = (z: number): string[] => {
    const diagram: string[] = [];
    const orbitals = ["1s", "2s", "2p", "3s", "3p"];
    const maxElectrons = [2, 2, 6, 2, 6];
    
    let remaining = Math.min(z, 18); // Limit to first 18 elements for simplicity
    
    for (let i = 0; i < orbitals.length && remaining > 0; i++) {
      const electrons = Math.min(remaining, maxElectrons[i]);
      const boxes = maxElectrons[i] / 2;
      let orbital = `${orbitals[i]}: `;
      
      for (let j = 0; j < boxes; j++) {
        if (electrons >= (j * 2 + 2)) {
          orbital += "[↑↓] ";
        } else if (electrons === (j * 2 + 1)) {
          orbital += "[↑ ] ";
        } else {
          orbital += "[ ] ";
        }
      }
      
      diagram.push(orbital);
      remaining -= electrons;
    }
    
    return diagram;
  };

  const calculate = () => {
    const z = parseInt(atomicNumber);

    if (isNaN(z) || z < 1 || z > 18) {
      toast({
        title: "Invalid Input",
        description: "Please enter an atomic number between 1 and 18.",
        variant: "destructive",
      });
      return;
    }

    const diagram = generateOrbitalDiagram(z);
    setResult(diagram);

    toast({
      title: "Diagram Generated",
      description: `Atomic number: ${z}`,
    });
  };

  const reset = () => {
    setAtomicNumber("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Orbital Diagram Generator" 
      calculatorPath="/calculator/orbital-diagram"
      category="chemistry"
      subcategory="Atomic Structure"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Orbital Diagram Generator</CardTitle>
            <CardDescription>
              Visualize electron orbitals (Z = 1-18)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="atomicNumber">Atomic Number (Z)</Label>
                <Input
                  id="atomicNumber"
                  type="number"
                  step="1"
                  value={atomicNumber}
                  onChange={(e) => setAtomicNumber(e.target.value)}
                  placeholder="Enter atomic number (1-18)"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Generate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Orbital Diagram</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {result.map((line, index) => (
                    <p key={index} className="font-mono text-sm">{line}</p>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Key</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-1">
                <p>[↑↓] = Paired electrons (opposite spins)</p>
                <p>[↑ ] = Unpaired electron</p>
                <p>[ ] = Empty orbital</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
